import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { createPortalApi, getBridgeOid } from '../api/portalApi';
import { useRuntimeConfig } from '../context/RuntimeConfigContext';
import { Icon } from '../components/icons';
import EmptyState from '../components/EmptyState';
import NoSessionsIllustration from '../components/illustrations/NoSessionsIllustration';
import type { ReviewSession } from '../types';

const DEV_BYPASS = import.meta.env.VITE_DEV_BYPASS_AUTH === 'true';
const DEV_EMAIL  = 'dev@fintinc.com';

const STATUS_LABEL: Record<string, { label: string; cls: string }> = {
  in_review: { label: 'In Review',  cls: 'badge-review'  },
  approved:  { label: 'Approved',   cls: 'badge-approved' },
  pushed:    { label: 'Pushed',     cls: 'badge-pushed'   },
  partial:   { label: 'Partial',    cls: 'badge-partial'  },
};

type SortCol = 'status' | 'createdAt' | 'updatedAt';
type SortDir = 'asc' | 'desc';

function formatDate(iso?: string) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function timeAgo(iso?: string) {
  if (!iso) return '—';
  const diff  = Date.now() - new Date(iso).getTime();
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  if (days  > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (mins  > 0) return `${mins}m ago`;
  return 'just now';
}

export default function AssignedForReviewPage() {
  const { instance, accounts } = useMsal();
  const cfg      = useRuntimeConfig();
  const navigate = useNavigate();
  const api      = createPortalApi(instance, cfg.msalClientId, cfg.apiBaseUrl);

  const currentUserId: string  = DEV_BYPASS ? '' : (accounts[0]?.localAccountId ?? getBridgeOid());
  const currentEmail: string   = DEV_BYPASS ? '' : (accounts[0]?.username ?? '').toLowerCase();

  const [sessions, setSessions] = useState<ReviewSession[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');

  const [sort, setSort]         = useState<{ col: SortCol; dir: SortDir } | null>(null);
  const [statusFilter, setStatus] = useState('');

  useEffect(() => {
    api.getSessions()
      .then(all => setSessions(
        all.filter(s => {
          // Check both OID (new format) and email (legacy format stored before the OID fix)
          const isAssigned = s.assignedReviewers?.some(
            r => r === currentUserId || (currentEmail && r.toLowerCase() === currentEmail),
          ) ?? false;
          return isAssigned && s.authorOid !== currentUserId;
        }),
      ))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId]);

  function toggleSort(col: SortCol) {
    setSort(prev => {
      if (prev?.col === col) return prev.dir === 'asc' ? { col, dir: 'desc' } : null;
      return { col, dir: 'asc' };
    });
  }

  function sortIcon(col: SortCol) {
    if (sort?.col !== col) return <span className="sort-icon sort-icon-idle">↕</span>;
    return <span className="sort-icon sort-icon-active">{sort.dir === 'asc' ? '↑' : '↓'}</span>;
  }

  const filtered = statusFilter ? sessions.filter(s => s.status === statusFilter) : sessions;
  const sorted   = sort
    ? [...filtered].sort((a, b) => {
        if (sort.col === 'status') {
          const r = a.status.localeCompare(b.status);
          return sort.dir === 'asc' ? r : -r;
        }
        const ta = new Date(sort.col === 'updatedAt' ? (a.updatedAt ?? a.createdAt) : a.createdAt).getTime();
        const tb = new Date(sort.col === 'updatedAt' ? (b.updatedAt ?? b.createdAt) : b.createdAt).getTime();
        return sort.dir === 'asc' ? ta - tb : tb - ta;
      })
    : filtered;

  if (loading) return <div className="page-loading"><span className="spinner" />Loading…</div>;
  if (error)   return <div className="page-error">Failed to load: {error}</div>;

  return (
    <>
      {/* Filter bar */}
      <div className="filter-bar">
        <label className="filter-label">Status</label>
        <select
          className="filter-select"
          value={statusFilter}
          onChange={e => setStatus(e.target.value)}
        >
          <option value="">All</option>
          <option value="in_review">In Review</option>
          <option value="approved">Approved</option>
          <option value="partial">Partial</option>
          <option value="pushed">Pushed</option>
        </select>
        {statusFilter && (
          <button className="btn btn-sm btn-outline" onClick={() => setStatus('')}>Clear</button>
        )}
        <span className="filter-count">{sorted.length} session{sorted.length !== 1 ? 's' : ''}</span>
      </div>

      {sorted.length === 0 ? (
        <EmptyState illustration={<NoSessionsIllustration />}>
          {statusFilter
            ? `No sessions with status "${STATUS_LABEL[statusFilter]?.label ?? statusFilter}".`
            : 'No sessions assigned to you for review.'}
        </EmptyState>
      ) : (
        <div className="sessions-table-wrapper">
          <table className="sessions-table">
            <thead>
              <tr>
                <th>Session</th>
                <th>Submitted by</th>
                <th>Cases</th>
                <th>Approved</th>
                <th
                  className="th-sortable"
                  onClick={() => toggleSort('status')}
                  title="Sort by status"
                >
                  Status {sortIcon('status')}
                </th>
                <th
                  className="th-sortable"
                  onClick={() => toggleSort('createdAt')}
                  title="Sort by submitted date"
                >
                  Submitted {sortIcon('createdAt')}
                </th>
                <th
                  className="th-sortable"
                  onClick={() => toggleSort('updatedAt')}
                  title="Sort by last reviewed date"
                >
                  Reviewed {sortIcon('updatedAt')}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(s => {
                const badge = STATUS_LABEL[s.status] ?? { label: s.status, cls: 'badge-review' };
                return (
                  <tr
                    key={s.sessionId}
                    className="session-row"
                    onClick={() => navigate(`/sessions/${s.sessionId}`)}
                  >
                    <td className="session-title-cell">
                      <div className="session-title-cell-inner">
                        <span className="session-name">{s.title}</span>
                        <span className="session-type">{s.outputType}</span>
                        {s.comment && (
                          <span className="session-comment" title={s.comment}><Icon name="message" size={11} /> {s.comment}</span>
                        )}
                      </div>
                    </td>
                    <td className="session-author">
                      <div className="session-author-inner">
                        <span className="avatar">
                          {(s.authorName || s.authorEmail)[0].toUpperCase()}
                        </span>
                        {s.authorEmail}
                      </div>
                    </td>
                    <td className="tc-count">{s.totalCases}</td>
                    <td className="tc-count">
                      <span className={s.approvedCount === s.totalCases ? 'all-approved' : ''}>
                        {s.approvedCount}/{s.totalCases}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${badge.cls}`}>{badge.label}</span>
                    </td>
                    <td
                      className="session-date"
                      title={formatDate(s.createdAt)}
                    >
                      {timeAgo(s.createdAt)}
                    </td>
                    <td
                      className="session-date"
                      title={formatDate(s.updatedAt)}
                    >
                      {s.updatedAt ? timeAgo(s.updatedAt) : '—'}
                    </td>
                    <td className="session-actions" onClick={e => e.stopPropagation()}>
                      <button
                        className="btn btn-sm btn-outline"
                        onClick={() => navigate(`/sessions/${s.sessionId}`)}
                      >
                        Review →
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
